import { useEffect, useState } from 'react';
import { useList, useOne } from '@refinedev/core';
import { Button, Drawer, Spin } from 'antd';
import { CheckIcon, GearIcon } from '@phosphor-icons/react';
import { FacilityType, type IERPUser, type IFacility } from '@admin-panel/types';
import { useGlobalContext } from '@admin-panel/hooks';

/** One big button per facility the user works at; tapping one makes it the working facility. */
export const FacilityButtons = ({
  user,
  onPicked,
}: {
  user: IERPUser | undefined;
  onPicked?: () => void;
}) => {
  const { warehouseId, facilityChosen, setWarehouseId } = useGlobalContext();
  const ids = user?.adminUserAttributes?.warehouseIds ?? [];
  const { result, query } = useList<IFacility>({
    resource: 'facilities',
    filters: [
      { field: 'id', operator: 'in', value: ids },
      {
        field: 'facilityType',
        operator: 'in',
        value: [FacilityType.WAREHOUSE, FacilityType.STORE],
      },
      { field: 'enabled', operator: 'eq', value: 'true' },
    ],
    sorters: [{ field: 'name', order: 'asc' }],
    pagination: { pageSize: 100 },
    queryOptions: { enabled: ids.length > 0 },
  });

  if (query.isLoading) return <Spin style={{ display: 'block', margin: '24px auto' }} />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {(result?.data ?? []).map(facility => {
        const current = facilityChosen && facility.id === warehouseId;
        return (
          <Button
            key={facility.id}
            size="large"
            type={current ? 'primary' : 'default'}
            icon={current ? <CheckIcon weight="bold" /> : undefined}
            iconPosition="end"
            style={{ height: 56, fontSize: 16, justifyContent: 'space-between' }}
            onClick={() => {
              setWarehouseId(facility.id as number);
              onPicked?.();
            }}
          >
            <span style={{ flex: 1, textAlign: 'left' }}>{facility.name}</span>
          </Button>
        );
      })}
    </div>
  );
};

/** The working facility's name in small grey text, for the phone's top bar. */
export const CurrentFacilityName = () => {
  const { warehouseId, isAllFacilities, facilityChosen } = useGlobalContext();
  const { result } = useOne<IFacility>({
    resource: 'facilities',
    id: warehouseId as number,
    queryOptions: { enabled: typeof warehouseId === 'number' },
  });
  // Only the chosen facility's own name: the last one's, still on screen while the new one loads, named the wrong
  // place (round 16, Ravi: "Dwarka Warehouse" right after choosing Brigade Orion).
  const name = isAllFacilities
    ? 'All facilities'
    : result && Number(result.id) === warehouseId
      ? result.name
      : undefined;
  if (!facilityChosen || !name) return null;
  return (
    <div
      style={{
        fontSize: 12,
        color: 'rgba(0,0,0,0.55)',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {name}
    </div>
  );
};

/** The phone's first screen until a facility is chosen. */
export const FacilityFirstPick = ({ user }: { user: IERPUser | undefined }) => (
  <div style={{ padding: '24px 4px' }}>
    <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 16 }}>Where are you working?</div>
    <FacilityButtons user={user} />
  </div>
);

/** A gear at the top of the phone to change facility later; hidden when there is only one. */
export const FacilitySettingsButton = ({ user }: { user: IERPUser | undefined }) => {
  const [open, setOpen] = useState(false);
  const [scanBlocked, setScanBlocked] = useState(false);
  // While the sheet is open a hand-scanner read never reaches the page under it unseen (round 19, Manoj: a read there
  // changed Quick Pick's shelf behind the sheet): the Hub's scanner holds it (data-scan-hold) and it is said here.
  useEffect(() => {
    if (!open) {
      setScanBlocked(false);
      return;
    }
    const held = () => setScanBlocked(true);
    window.addEventListener('hub-scan-held', held);
    return () => window.removeEventListener('hub-scan-held', held);
  }, [open]);
  if ((user?.adminUserAttributes?.warehouseIds?.length ?? 0) < 2) return null;
  return (
    <>
      <Button
        type="text"
        aria-label="Change facility"
        icon={<GearIcon size={22} />}
        onClick={() => setOpen(true)}
      />
      <Drawer
        title="Facility"
        placement="bottom"
        height="auto"
        open={open}
        onClose={() => setOpen(false)}
        // A full thumb-size Close (round 18: the drawer's 24 px X).
        closable={false}
        extra={
          <Button
            size="large"
            style={{ minWidth: 48, minHeight: 48 }}
            onClick={() => setOpen(false)}
          >
            Close
          </Button>
        }
        destroyOnHidden
        styles={{ content: { borderTopLeftRadius: 20, borderTopRightRadius: 20 } }}
      >
        {scanBlocked && (
          <div
            role="alert"
            data-testid="facility-scan-blocked"
            style={{ marginBottom: 12, fontSize: 16, fontWeight: 600, color: '#a8071a' }}
          >
            A scan came in and was not used. Close this, then scan it again.
          </div>
        )}
        <div data-scan-hold>
          <FacilityButtons user={user} onPicked={() => setOpen(false)} />
        </div>
      </Drawer>
    </>
  );
};
